import { assetURL } from "../assets";
import { type EquipmentSlot, type EquipmentTier } from "../types";

export const LEGENDARY_ICON_PATHS: Record<string, string> = {
  greedy_pick: assetURL("/assets/source/legendary/greedy-pick.png"),
  echo_staff: assetURL("/assets/source/legendary/echo-staff.png"),
  abyss_pact: assetURL("/assets/source/legendary/abyss-pact.png"),
  oathkeeper: assetURL("/assets/source/legendary/oathkeeper.png"),
  key_spirit: assetURL("/assets/source/legendary/key-spirit.png"),
  chain_core: assetURL("/assets/source/legendary/chain-core.png"),
  clockmaker: assetURL("/assets/source/legendary/clockmaker.png"),
  rockheart: assetURL("/assets/source/legendary/rockheart.png"),
  echo_vein: assetURL("/assets/source/legendary/echo-vein.png"),
  unsolvable: assetURL("/assets/source/legendary/unsolvable.png"),
  last_bastion: assetURL("/assets/source/legendary/last-bastion.png"),
  rune_grip: assetURL("/assets/source/legendary/rune-grip.png"),
};

export const TIER_EQUIPMENT_ICON_PATHS: Record<EquipmentTier, Record<EquipmentSlot, string>> = {
  t1: {
    weapon: assetURL("/assets/source/equipment/t1/weapon.png"),
    helmet: assetURL("/assets/source/equipment/t1/helmet.png"),
    armor: assetURL("/assets/source/equipment/t1/armor.png"),
    gloves: assetURL("/assets/source/equipment/t1/gloves.png"),
    boots: assetURL("/assets/source/equipment/t1/boots.png"),
    amulet: assetURL("/assets/source/equipment/t1/amulet.png"),
  },
  t2: {
    weapon: assetURL("/assets/source/equipment/t2/weapon.png"),
    helmet: assetURL("/assets/source/equipment/t2/helmet.png"),
    armor: assetURL("/assets/source/equipment/t2/armor.png"),
    gloves: assetURL("/assets/source/equipment/t2/gloves.png"),
    boots: assetURL("/assets/source/equipment/t2/boots.png"),
    amulet: assetURL("/assets/source/equipment/t2/amulet.png"),
  },
  t3: {
    weapon: assetURL("/assets/source/equipment/t3/weapon.png"),
    helmet: assetURL("/assets/source/equipment/t3/helmet.png"),
    armor: assetURL("/assets/source/equipment/t3/armor.png"),
    gloves: assetURL("/assets/source/equipment/t3/gloves.png"),
    boots: assetURL("/assets/source/equipment/t3/boots.png"),
    amulet: assetURL("/assets/source/equipment/t3/amulet.png"),
  },
  t4: {
    weapon: assetURL("/assets/source/equipment/t4/weapon.png"),
    helmet: assetURL("/assets/source/equipment/t4/helmet.png"),
    armor: assetURL("/assets/source/equipment/t4/armor.png"),
    gloves: assetURL("/assets/source/equipment/t4/gloves.png"),
    boots: assetURL("/assets/source/equipment/t4/boots.png"),
    amulet: assetURL("/assets/source/equipment/t4/amulet.png"),
  },
  t5: {
    weapon: assetURL("/assets/source/equipment/t5/weapon.png"),
    helmet: assetURL("/assets/source/equipment/t5/helmet.png"),
    armor: assetURL("/assets/source/equipment/t5/armor.png"),
    gloves: assetURL("/assets/source/equipment/t5/gloves.png"),
    boots: assetURL("/assets/source/equipment/t5/boots.png"),
    amulet: assetURL("/assets/source/equipment/t5/amulet.png"),
  },
};

export const BATTLE_SCENE_PATHS: Record<string, string> = {
  drifts: assetURL("/assets/scenes/battle-drifts.png"),
  wetcave: assetURL("/assets/scenes/battle-wetcave.png"),
  mycelium: assetURL("/assets/scenes/battle-mycelium.png"),
  rustworks: assetURL("/assets/scenes/battle-rustworks.png"),
  crystal: assetURL("/assets/scenes/battle-crystal.png"),
  magma: assetURL("/assets/scenes/battle-magma.png"),
  saltvault: assetURL("/assets/scenes/battle-wetcave.png"),
  emberroot: assetURL("/assets/scenes/battle-mycelium.png"),
  mirror_fault: assetURL("/assets/scenes/battle-crystal.png"),
  blackforge: assetURL("/assets/scenes/battle-rustworks.png"),
};

export const MONSTER_SPRITE_PATHS: Record<string, string> = {
  mine_rat: assetURL("/assets/sprites/mine-rat.png"),
  cave_bat: assetURL("/assets/sprites/cave-bat.png"),
  rock_beetle: assetURL("/assets/sprites/rock-beetle.png"),
  tunnel_tick: assetURL("/assets/source/regions/drifts/tunnel-tick.png"),
  rubble_husk: assetURL("/assets/source/regions/drifts/rubble-husk.png"),
  dust_wisp: assetURL("/assets/source/regions/drifts/dust-wisp.png"),
  flint_spirit: assetURL("/assets/source/regions/drifts/flint-spirit.png"),
  sump_toad: assetURL("/assets/source/regions/wetcave/sump-toad.png"),
  brine_eel: assetURL("/assets/source/regions/wetcave/brine-eel.png"),
  mire_newt: assetURL("/assets/source/regions/wetcave/mire-newt.png"),
  spore_cap: assetURL("/assets/source/regions/mycelium/spore-cap.png"),
  creep_vine: assetURL("/assets/source/regions/mycelium/creep-vine.png"),
  iron_mold: assetURL("/assets/source/regions/mycelium/iron-mold.png"),
  rust_sentinel: assetURL("/assets/source/regions/rustworks/rust-sentinel.png"),
  gear_wraith: assetURL("/assets/source/regions/rustworks/gear-wraith.png"),
  chain_warden: assetURL("/assets/source/regions/rustworks/rust-sentinel.png"),
  prism_ward: assetURL("/assets/source/regions/crystal/prism-ward.png"),
  shard_swarm: assetURL("/assets/source/regions/crystal/shard-swarm.png"),
  cold_prism: assetURL("/assets/source/regions/crystal/prism-ward.png"),
  cinder_maw: assetURL("/assets/source/regions/magma/cinder-maw.png"),
  magma_crawler: assetURL("/assets/source/regions/magma/magma-crawler.png"),
  slag_brute: assetURL("/assets/source/regions/magma/magma-crawler.png"),
};

export const MONSTER_FAMILY_FALLBACKS: Record<string, string> = {
  vermin: MONSTER_SPRITE_PATHS.mine_rat,
  collapse: MONSTER_SPRITE_PATHS.rubble_husk,
  amphibian: MONSTER_SPRITE_PATHS.sump_toad,
  fungal: MONSTER_SPRITE_PATHS.spore_cap,
  construct: MONSTER_SPRITE_PATHS.rust_sentinel,
  crystalline: MONSTER_SPRITE_PATHS.prism_ward,
  magmatic: MONSTER_SPRITE_PATHS.magma_crawler,
  undead: MONSTER_SPRITE_PATHS.gear_wraith,
  echo: MONSTER_SPRITE_PATHS.dust_wisp,
  hollow: MONSTER_SPRITE_PATHS.cinder_maw,
  abyssal: MONSTER_SPRITE_PATHS.creep_vine,
};

export const RAID_BOSS_SPRITE_PATHS: Record<string, string> = {
  crystal_colossus: assetURL("/assets/source/raid/crystal-colossus.png"),
  root_mother: assetURL("/assets/source/raid/root-mother.png"),
  abyss_echo: assetURL("/assets/source/raid/abyss-echo.png"),
  rust_engine: assetURL("/assets/source/raid/rust-engine.png"),
};

export const FLOOR_BOSS_SPRITE_PATHS: Record<string, string> = {
  boss_pitmaster: assetURL("/assets/sprites/pitmaster.png"),
  boss_broodqueen: MONSTER_SPRITE_PATHS.tunnel_tick,
  boss_floodmaw: MONSTER_SPRITE_PATHS.sump_toad,
  boss_sporecrown: RAID_BOSS_SPRITE_PATHS.root_mother,
  boss_rustlord: RAID_BOSS_SPRITE_PATHS.rust_engine,
  boss_prismheart: RAID_BOSS_SPRITE_PATHS.crystal_colossus,
  boss_forgewyrm: MONSTER_SPRITE_PATHS.magma_crawler,
  boss_bonechoir: MONSTER_SPRITE_PATHS.gear_wraith,
  boss_echoself: RAID_BOSS_SPRITE_PATHS.abyss_echo,
  boss_hollowking: MONSTER_SPRITE_PATHS.rubble_husk,
};

export function battleSceneForZone(zoneId: string): string {
  return BATTLE_SCENE_PATHS[zoneId] ?? assetURL("/assets/scenes/mine-battle.png");
}

export function spriteForMonster(monster: { id: string; boss: boolean; family?: string }): string {
  if (monster.boss)
    return FLOOR_BOSS_SPRITE_PATHS[monster.id] ?? assetURL("/assets/sprites/pitmaster.png");
  return (
    MONSTER_SPRITE_PATHS[monster.id] ??
    MONSTER_FAMILY_FALLBACKS[monster.family ?? ""] ??
    MONSTER_SPRITE_PATHS.mine_rat
  );
}

export function spriteForRaidBoss(bossId: string): string {
  return RAID_BOSS_SPRITE_PATHS[bossId] ?? assetURL("/assets/sprites/pitmaster.png");
}
